/**
 * Partition a multi-section room's floor polygon into per-section
 * polygons, using a Voronoi-style nearest-center rule.
 *
 * Each `roomData.sections` entry has a `center` and `label`. The
 * Voronoi diagram of those centers, clipped to the floor polygon,
 * yields one polygon per section: the locus of points inside the
 * floor polygon for which that section's center is the nearest.
 *
 * Used by:
 *   - The kitchen-layout debug visualization (overlay).
 *   - (future) island / peninsula placement, where we need the
 *     kitchen's interior region to drop free-standing cabinets.
 *
 * Walls are still assigned to sections via the cheaper probe approach
 * in `kitchen-assembly/kitchenPerimeter.js` — this module is for
 * region-level reasoning, not wall labelling.
 *
 * Floor polygons are assumed CCW (RoomPlan -> processFloors reverses
 * RoomPlan's CW input). Voronoi cells are constructed CCW from the
 * bounding box; SH clipping preserves orientation.
 *
 * Pure: takes data in, returns polygons. No external deps.
 */

/**
 * Generic 2D point: { x, z }. (Floor polygon corners are in the X-Z
 * plane; Y is height/up. Methods here ignore Y.)
 */

/**
 * Compute the Voronoi cell for the i-th center as the intersection of
 * half-planes "closer to centers[i] than to centers[j]" for every
 * j ≠ i. Starts with a large bounding rectangle and clips down by
 * each bisector half-plane.
 *
 * Returns the cell as a CCW polygon (array of {x, z}). Empty if the
 * cell is degenerate (shouldn't happen for distinct centers within a
 * finite bbox).
 */
function computeVoronoiCell(centers, i, bbox) {
  let cell = bboxToPolygon(bbox);
  for (let j = 0; j < centers.length; j++) {
    if (j === i) continue;
    cell = clipByBisector(cell, centers[i], centers[j]);
    if (cell.length === 0) return [];
  }
  return cell;
}

/**
 * Sutherland-Hodgman clip of `polygon` against the half-plane "closer
 * to p than to q". The bisector of p and q is perpendicular to (q-p)
 * through the midpoint; the kept side is the one containing p.
 */
function clipByBisector(polygon, p, q) {
  // Half-plane: a*x + c*z + d <= 0, where (a, c) = q - p and
  // d = -((p+q)/2) · (q-p). Points on the bisector itself are kept.
  const a = q.x - p.x;
  const c = q.z - p.z;
  const mx = (p.x + q.x) / 2;
  const mz = (p.z + q.z) / 2;
  const d = -(a * mx + c * mz);

  const inside = (pt) => a * pt.x + c * pt.z + d <= 0;
  const intersect = (pa, pb) => {
    const dx = pb.x - pa.x;
    const dz = pb.z - pa.z;
    const denom = a * dx + c * dz;
    if (denom === 0) return pa; // parallel: degenerate fallback
    const t = -(a * pa.x + c * pa.z + d) / denom;
    return { x: pa.x + t * dx, z: pa.z + t * dz };
  };

  return clipBySingleEdge(polygon, inside, intersect);
}

/**
 * Generic single-edge SH clip. `inside(pt)` decides which side to
 * keep; `intersect(pa, pb)` returns the crossing point of segment
 * pa–pb with the clip line.
 */
function clipBySingleEdge(polygon, inside, intersect) {
  if (polygon.length === 0) return [];
  const out = [];
  let prev = polygon[polygon.length - 1];
  let prevIn = inside(prev);
  for (const curr of polygon) {
    const currIn = inside(curr);
    if (currIn) {
      if (!prevIn) out.push(intersect(prev, curr));
      out.push(curr);
    } else if (prevIn) {
      out.push(intersect(prev, curr));
    }
    prev = curr;
    prevIn = currIn;
  }
  return out;
}

/**
 * Sutherland-Hodgman: clip subject polygon by a CONVEX clip polygon,
 * one clip edge at a time. Subject can be any simple polygon (the
 * floor polygon may be non-convex). Returns a single polygon — for
 * non-convex floors whose intersection with the cell would be
 * disconnected, SH may emit a polygon with collinear bridge edges
 * connecting the disconnected pieces, which is acceptable for the
 * region-overlay use case.
 */
function clipPolygonByConvex(subject, clip) {
  if (subject.length === 0 || clip.length === 0) return [];
  let out = subject;
  for (let i = 0; i < clip.length; i++) {
    if (out.length === 0) return [];
    const a = clip[i];
    const b = clip[(i + 1) % clip.length];
    out = clipBySingleEdge(
      out,
      // Inside = on the left of edge a→b for a CCW clip polygon.
      (pt) => crossZ(a, b, pt) >= 0,
      (pa, pb) => segmentIntersect(pa, pb, a, b)
    );
  }
  return out;
}

// 2D cross product (z-up) of (b - a) and (p - a). Sign tells which
// side of the directed line a→b the point p lies on.
function crossZ(a, b, p) {
  return (b.x - a.x) * (p.z - a.z) - (b.z - a.z) * (p.x - a.x);
}

// Intersection of segment pa→pb with the infinite line through a→b.
function segmentIntersect(pa, pb, a, b) {
  const dx1 = pb.x - pa.x;
  const dz1 = pb.z - pa.z;
  const dx2 = b.x - a.x;
  const dz2 = b.z - a.z;
  const denom = dx1 * dz2 - dz1 * dx2;
  if (denom === 0) return pa; // parallel: degenerate fallback
  const t = ((a.x - pa.x) * dz2 - (a.z - pa.z) * dx2) / denom;
  return { x: pa.x + t * dx1, z: pa.z + t * dz1 };
}

function bboxToPolygon({ minX, minZ, maxX, maxZ }) {
  // CCW order in the X-Z plane: low-x/low-z, high-x/low-z, high-x/high-z, low-x/high-z.
  return [
    { x: minX, z: minZ },
    { x: maxX, z: minZ },
    { x: maxX, z: maxZ },
    { x: minX, z: maxZ },
  ];
}

function computeBoundingBox(corners, centers) {
  let minX = Infinity;
  let minZ = Infinity;
  let maxX = -Infinity;
  let maxZ = -Infinity;
  for (const p of corners) {
    if (p.x < minX) minX = p.x;
    if (p.x > maxX) maxX = p.x;
    if (p.z < minZ) minZ = p.z;
    if (p.z > maxZ) maxZ = p.z;
  }
  for (const c of centers) {
    if (c.x < minX) minX = c.x;
    if (c.x > maxX) maxX = c.x;
    if (c.z < minZ) minZ = c.z;
    if (c.z > maxZ) maxZ = c.z;
  }
  // Pad so Voronoi cells extend well beyond the floor polygon.
  const pad = Math.max(maxX - minX, maxZ - minZ, 1);
  return {
    minX: minX - pad,
    minZ: minZ - pad,
    maxX: maxX + pad,
    maxZ: maxZ + pad,
  };
}

/**
 * @param {Object} roomData - room with `sections` and `floors`
 * @returns {Map<string, Array<Array<{x, z}>>>} sectionLabel → list of
 *   polygons (each polygon is an array of {x, z} corners in CCW order).
 *   Multiple sections sharing the same label append to the list.
 *   Returns an empty map if the room has no floor polygon or no
 *   sections with a center.
 */
function getSectionPolygons(roomData) {
  const sections = (roomData?.sections || []).filter(
    (s) => s?.center && s?.label
  );
  const floorCorners = roomData?.floors?.[0]?.polygonCorners;

  if (!floorCorners || floorCorners.length < 3) return new Map();
  if (sections.length === 0) return new Map();

  const result = new Map();

  // Single-section room: whole floor → that section. Skip Voronoi.
  if (sections.length === 1) {
    result.set(sections[0].label, [
      floorCorners.map((p) => ({ x: p.x, z: p.z })),
    ]);
    return result;
  }

  const centers = sections.map((s) => ({ x: s.center.x, z: s.center.z }));
  const bbox = computeBoundingBox(floorCorners, centers);

  for (let i = 0; i < sections.length; i++) {
    const cell = computeVoronoiCell(centers, i, bbox);
    if (cell.length < 3) continue;

    const clipped = clipPolygonByConvex(floorCorners, cell);
    if (clipped.length < 3) continue;

    // Multiple sections may share a label — append rather than overwrite.
    const existing = result.get(sections[i].label) || [];
    existing.push(clipped);
    result.set(sections[i].label, existing);
  }

  return result;
}

module.exports = {
  getSectionPolygons,
  // Exposed for tests / debugging — production callers should use
  // getSectionPolygons.
  computeVoronoiCell,
  clipPolygonByConvex,
};
